import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  ENTITY_IDENTIFICATION_TYPE_OPTIONS,
  LEGAL_STRUCTURE_OPTIONS,
} from 'src/constants/payments-ops/bankOnboarding'
import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'

// `css` on a Formik field lands on the inner input, not the grid cell — spans use a wrapper div.
const BusinessStepComponent = () => {
  const { values } = useFormikContext<BankOnboardingFormValues>()

  return (
    <div css={tw`flex flex-col gap-4`}>
      <div css={tw`grid grid-cols-2 gap-4`}>
        <div css={tw`col-span-2`}>
          <FormikTextField<BankOnboardingFormValues>
            name="legalName"
            label="Legal business name *"
            placeholder="Legal name"
          />
        </div>
        <FormikSelect<BankOnboardingFormValues>
          name="entityIdentificationType"
          label="Identification type *"
          options={ENTITY_IDENTIFICATION_TYPE_OPTIONS}
          placeholder="EIN or TIN"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="entityIdentificationNumber"
          label="Identification number *"
          placeholder="9 digits"
        />
        <div css={tw`col-span-2`}>
          <FormikSelect<BankOnboardingFormValues>
            name="legalStructure"
            label="Legal structure *"
            options={LEGAL_STRUCTURE_OPTIONS}
            placeholder="Choose a legal structure"
          />
        </div>
        <FormikTextField<BankOnboardingFormValues>
          name="naicsCode"
          label="NAICS code *"
          placeholder="2–6 digits, e.g. 484110"
          inputProps={{ inputMode: 'numeric' }}
        />
        <FormikTextField<BankOnboardingFormValues>
          name="natureOfBusiness"
          label="Nature of business *"
          placeholder="e.g. Freight delivery"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="projectedAnnualRevenue"
          label="Projected annual revenue ($) *"
          placeholder="Whole dollars, e.g. 2,500,000"
          inputProps={{ inputMode: 'numeric' }}
        />
        <FormikTextField<BankOnboardingFormValues>
          name="businessPhoneNumber"
          label="Business phone *"
          placeholder="10 digits"
          inputProps={{ inputMode: 'tel' }}
        />
        <FormikTextField<BankOnboardingFormValues>
          name="countryOfFormation"
          label="Country of formation *"
          placeholder="US"
          inputProps={{ maxLength: 2 }}
        />
        <FormikTextField<BankOnboardingFormValues>
          name="countryOfPrimaryBusinessOperations"
          label="Country of primary operations *"
          placeholder="US"
          inputProps={{ maxLength: 2 }}
        />
      </div>
      <FormikSwitch<BankOnboardingFormValues> name="hasAnyDba" label="Operates under a DBA" />
      {values.hasAnyDba ? (
        <FormikTextField<BankOnboardingFormValues>
          name="dbaName"
          label="DBA name *"
          placeholder="Doing business as"
        />
      ) : null}
      <div css={tw`grid grid-cols-2 gap-4`}>
        <FormikTextField<BankOnboardingFormValues>
          name="publiclyTradedStockSymbol"
          label="Stock symbol"
          placeholder="If publicly traded"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="tradedExchange"
          label="Exchange"
          placeholder="If publicly traded"
        />
      </div>
      <Typography variant="h5" css={tw`mt-2`}>
        Compliance
      </Typography>
      <FormikSwitch<BankOnboardingFormValues>
        name="msbServices"
        label="Provides money services (MSB)"
      />
      <FormikSwitch<BankOnboardingFormValues> name="cipExempt" label="CIP exempt" />
      <FormikSwitch<BankOnboardingFormValues>
        name="hasAnyBeneficialOwner"
        label="Has beneficial owners (25%+)"
      />
      <FormikSwitch<BankOnboardingFormValues>
        name="beneficialOwnerExempt"
        label="Beneficial owner exempt"
      />
      <FormikCheckbox<BankOnboardingFormValues>
        name="programParticipantAttestation"
        label="Program participant attestation — required for every onboarding."
        disabled
      />
    </div>
  )
}

export const BusinessStep = memo(BusinessStepComponent)
